import { isPlayedDown } from '../../cms-blocks/SortAndFilterPanel/resolveEligibility';
import type { EligibilityVerdict } from '../../types/eligibility';

/**
 * Marks the cell holding the lender's logo, so the played-down treatment can
 * find it. Position cannot: CMS-1906 prepends a 24px label rail whenever any
 * product in the table carries a `row_label`, which moves `BANK_LOGO` off
 * first-child for every row in that table.
 */
export const PROVIDER_LOGO_CELL = 'provider-logo-cell';

/**
 * The comparison table's played-down row (CMS-1905 requirement 4b).
 *
 * Exported rather than written inline at the call site so a test can exercise
 * the string the table actually uses — an assertion against a copy of it
 * passes for ever, whatever the table does.
 *
 * Two details carry the requirement:
 *
 * - **The tint is on the cells.** A `<td>` paints its own background, so a
 *   row-level colour is covered and reads as no change at all.
 * - **`!` is load-bearing.** The cell also carries
 *   `group-hover/row:bg-fill/base`, which ties on specificity and wins on
 *   source order, so without it the tint lifts under the cursor (measured
 *   going 245 → 250 on hover).
 *
 * Greyscale is scoped to the logo cell by marker: the row also carries reward
 * art and a Google mark, which are not the lender's identity. Nothing fades,
 * because the Apply button sits in one of these cells and stays at full
 * prominence.
 */
export const playedDownRowClassNames = (
  verdict: EligibilityVerdict | undefined,
): string | undefined =>
  isPlayedDown(verdict)
    ? // Written out, not interpolated: Tailwind scans raw source text, so a
      // composed class name is never generated. The spec pins the two together.
      '[&>td]:!bg-fill/alternative-1 [&_.provider-logo-cell_img]:grayscale'
    : undefined;
